/**
 * U.S. Virgin Islands geographic constants — used by the PirateMap page and
 * any other surface that needs to plot listings or events on a real-world
 * coordinate system.
 *
 * Coordinates are approximate centroids drawn from public domain sources
 * (Wikipedia coordinate boxes) and are intentionally rounded to four decimal
 * places — sufficient for marker placement, not a substitute for real
 * geocoding. When a listing has no `latitude`/`longitude` field, the map
 * uses the island centroid (with a small deterministic jitter so co-located
 * markers don't fully overlap).
 */

export const USVI_BOUNDS = Object.freeze({
  // [west, south, east, north]
  bbox: [-65.1, 17.65, -64.55, 18.45],
  center: [-64.84, 18.05],
  defaultZoom: 9.5,
});

export const ISLAND_CENTROIDS = Object.freeze({
  stt: { lat: 18.3419, lng: -64.9307, label: "St. Thomas" },
  stj: { lat: 18.3358, lng: -64.7333, label: "St. John" },
  stx: { lat: 17.7338, lng: -64.7848, label: "St. Croix" },
  wj: { lat: 18.3239, lng: -64.9573, label: "Water Island" },
});

/**
 * Resolve an island key from a raw listing/event `island` value (which may
 * already be one of our keys or a freeform string like "St. Thomas").
 * @param {string|null|undefined} raw
 * @returns {string|null} island key (stt|stj|stx|wj) or null
 */
export function resolveIslandKey(raw) {
  if (!raw) return null;
  const lower = String(raw).toLowerCase().trim();
  if (ISLAND_CENTROIDS[lower]) return lower;
  if (lower.includes("thomas")) return "stt";
  if (lower.includes("john")) return "stj";
  if (lower.includes("croix")) return "stx";
  if (lower.includes("water")) return "wj";
  return null;
}

/**
 * Produce a deterministic small offset (in degrees) so multiple items
 * falling back to the same island centroid don't perfectly overlap. Uses a
 * cheap string hash so the same item always lands in the same spot.
 *
 * @param {string} key unique key (e.g. listing id)
 * @returns {{dLat:number, dLng:number}}
 */
export function jitterFor(key) {
  let hash = 0;
  const s = String(key || "");
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) & 0xffff;
  }
  const a = ((hash & 0xff) / 255 - 0.5) * 0.04; // ~ ±2 km
  const b = (((hash >> 8) & 0xff) / 255 - 0.5) * 0.04;
  return { dLat: a, dLng: b };
}

/**
 * Resolve plot coordinates for an arbitrary record.
 *
 * Resolution order:
 *   1. Numeric `latitude` + `longitude` on the record itself.
 *   2. Numeric `coordinates.latitude` + `.longitude` sub-object.
 *   3. If `options.resolveLinked` is provided and the record references a
 *      linked listing (e.g. an event with `locationListingId` or `businessId`),
 *      recursively resolve that listing's coords. The linked lookup is only
 *      followed one hop deep to avoid cycles.
 *   4. Island centroid (with deterministic jitter using the record id).
 *   5. null (caller should skip).
 *
 * @param {object} record listing or event
 * @param {{resolveLinked?: (id:string) => object|null|undefined}} [options]
 * @returns {{lat:number, lng:number, fallback:false|true|"linked"}|null}
 */
export function resolvePlotCoords(record, options) {
  if (!record) return null;
  const latRaw = record.latitude ?? record.coordinates?.latitude;
  const lngRaw = record.longitude ?? record.coordinates?.longitude;
  const lat = typeof latRaw === "number" ? latRaw : Number(latRaw);
  const lng = typeof lngRaw === "number" ? lngRaw : Number(lngRaw);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return { lat, lng, fallback: false };
  }
  // Try linked-listing fallback (events that reference a business location).
  const resolveLinked = options && options.resolveLinked;
  if (typeof resolveLinked === "function") {
    const linkId = record.locationListingId || record.businessId || null;
    if (linkId && linkId !== record.id) {
      const linked = resolveLinked(linkId);
      if (linked) {
        const lLat = linked.latitude ?? linked.coordinates?.latitude;
        const lLng = linked.longitude ?? linked.coordinates?.longitude;
        const lN = typeof lLat === "number" ? lLat : Number(lLat);
        const gN = typeof lLng === "number" ? lLng : Number(lLng);
        if (Number.isFinite(lN) && Number.isFinite(gN)) {
          return { lat: lN, lng: gN, fallback: "linked" };
        }
      }
    }
  }
  const islandKey = resolveIslandKey(record.island);
  if (islandKey) {
    const centroid = ISLAND_CENTROIDS[islandKey];
    const { dLat, dLng } = jitterFor(record.id || record.businessId || record.title);
    return { lat: centroid.lat + dLat, lng: centroid.lng + dLng, fallback: true };
  }
  return null;
}
